import {
  Autocomplete,
  Chip,
  ListItem,
  ListItemText,
  Typography,
  Stack,
  ChipProps,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import shadows from "@mui/material/styles/shadows";
import {
  CrossIcon,
  UserMultipleStrokeStandardIcon,
} from "@/components/common/icons";
import { GREY, VIOLET } from "@/config/mui/colorPalette";
import { MedspaProvidersQuery } from "@/graphql/queries/medspaProviders.graphql.types";

export type Provider =
  MedspaProvidersQuery["medspaByPk"]["userMedspas"][number];

type Props = {
  disabled: boolean;
  value: Provider[];
  options: Provider[];
  label?: string;
  onChange: (newValue: Provider[]) => void;
};

export const ProviderSection = ({
  disabled,
  value,
  options,
  label = "Provider(s) (optional)",
  onChange,
}: Props) => {
  return (
    <Stack gap={1.5}>
      <Typography variant="labelTiny" color="text.secondary">
        {label}
      </Typography>
      <Autocomplete<Provider, true>
        disablePortal
        multiple
        disabled={disabled}
        popupIcon={null}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="All providers"
            variant="outlined"
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <UserMultipleStrokeStandardIcon
                      key="icon"
                      color={GREY[50]}
                      size={24}
                    />
                    <Stack direction="column" gap={0.5} alignItems="flex-start">
                      {...Array.isArray(params.InputProps.startAdornment)
                        ? params.InputProps.startAdornment
                        : [params.InputProps.startAdornment]}
                    </Stack>
                  </Stack>
                ),
                size: "small",
                sx: {
                  backgroundColor: (theme) =>
                    params.disabled ? GREY[15] : theme.palette.background.paper,
                  pl: "12px !important",
                  py: "8px !important",
                },
              },
            }}
          />
        )}
        renderTags={(value: readonly Provider[], getTagProps) =>
          value.map((option: Provider, index: number) => {
            const { key, ...tagProps } = getTagProps({ index });

            return (
              <ProviderChip
                key={key}
                label={option.user.fullName}
                {...tagProps}
              />
            );
          })
        }
        slotProps={{
          paper: {
            sx: { boxShadow: shadows[4], borderRadius: 2, mt: 1 },
          },
        }}
        options={options || []}
        value={value}
        getOptionLabel={(option) => option.user.fullName}
        renderOption={(props, option) => (
          <ListItem
            component="li"
            {...props}
            key={option.id}
            sx={{ px: "12px !important", py: "8px !important" }}
          >
            <ListItemText
              sx={{ m: 0 }}
              primary={option.user.fullName}
              slotProps={{
                primary: { variant: "labelSmall" },
              }}
            />
          </ListItem>
        )}
        onChange={(_, value) => {
          onChange(value);
        }}
        clearIcon={
          <CrossIcon style={{ color: VIOLET[90] }} size={16} strokeWidth={2} />
        }
      />
    </Stack>
  );
};

const ProviderChip = ({ label, ...props }: ChipProps) => {
  return (
    <Chip
      variant="filled"
      sx={{
        backgroundColor: VIOLET[40],
        height: "24px",
        m: "0 !important",
        gap: 0.5,
        px: 1,
        py: 0.25,
        maxWidth: "100% !important",
        "& .MuiChip-label": {
          p: 0,
          pr: 1,
        },
        "& .MuiChip-deleteIcon": {
          m: 0,
        },
      }}
      label={
        <Typography
          color={VIOLET[100]}
          variant="labelSmall"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "224px",
          }}
        >
          {label}
        </Typography>
      }
      deleteIcon={
        <CrossIcon style={{ color: VIOLET[100] }} size={16} strokeWidth={2} />
      }
      {...props}
    />
  );
};
